import React, { useState, useRef } from "react";
import { Camera, Copy, Image as ImageIcon, Loader2, Check } from "lucide-react";

const App = () => {
  const [image, setImage] = useState(null);
  const [base64Image, setBase64Image] = useState(null);
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  const apiKey = process.env.REACT_APP_GEMINI_API_KEY; // Environment will provide this

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(URL.createObjectURL(file));
        setBase64Image(reader.result.split(",")[1]);
        setDescription("");
        setError(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const describeImage = async () => {
    if (!base64Image) return;

    setLoading(true);
    setError(null);
    setDescription("");

    const systemPrompt = `
      Describe the image in English with high detail but strictly follow these rules:
      1. DO NOT describe anything related to the head or face (no ethnicity, hair, hairstyle, hair color, mustache, beard, eyes, or facial features).
      2. Identify the main subject (gender or object) and immediately follow it with the phrase "(image reference)".
      3. Focus on clothing, posture, environment, actions, lighting, and background.
      4. DO NOT use any introductory words like "The image shows..." or "In this picture...". Start directly with the subject.
      5. Format Example: "A man (image reference) wearing a blue denim jacket standing in a crowded subway station..."
    `;

    const payload = {
      contents: [
        {
          role: "user",
          parts: [
            {
              text: "Describe this image following the strict rules provided.",
            },
            { inlineData: { mimeType: "image/png", data: base64Image } },
          ],
        },
      ],
      systemInstruction: {
        parts: [{ text: systemPrompt }],
      },
    };

    let retries = 0;
    const maxRetries = 5;

    const makeRequest = async () => {
      try {
        const response = await fetch(
          `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-09-2025:generateContent?key=${apiKey}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          }
        );

        if (!response.ok) throw new Error("API request failed");

        const result = await response.json();
        const text = result.candidates?.[0]?.content?.parts?.[0]?.text;

        if (text) {
          setDescription(text.trim());
        } else {
          throw new Error("No description generated");
        }
      } catch (err) {
        if (retries < maxRetries) {
          retries++;
          const delay = Math.pow(2, retries) * 1000;
          await new Promise((resolve) => setTimeout(resolve, delay));
          return makeRequest();
        }
        setError("Gagal mendeskripsikan gambar. Silakan coba lagi nanti.");
      } finally {
        setLoading(false);
      }
    };

    await makeRequest();
  };

  const copyToClipboard = () => {
    const textArea = document.createElement("textarea");
    textArea.value = description;
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand("copy");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Fallback copy failed", err);
    }
    document.body.removeChild(textArea);
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8 flex flex-col items-center">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-slate-100 text-center">
          <h1 className="text-2xl font-bold text-slate-800">Image Describer</h1>
          <p className="text-slate-500 text-sm mt-1">
            Deskripsi detail tanpa detail wajah/kepala
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* Upload Area */}
          <div
            className={`relative border-2 border-dashed rounded-xl p-4 transition-all flex flex-col items-center justify-center min-h-[250px] ${
              image
                ? "border-blue-200 bg-blue-50"
                : "border-slate-300 hover:border-blue-400"
            }`}
          >
            {image ? (
              <div className="relative w-full h-full">
                <img
                  src={image}
                  alt="Preview"
                  className="max-h-64 mx-auto rounded-lg shadow-sm object-contain"
                />
                <button
                  onClick={() => {
                    setImage(null);
                    setBase64Image(null);
                    setDescription("");
                  }}
                  className="absolute top-2 right-2 bg-white/80 backdrop-blur-sm p-1.5 rounded-full shadow-sm hover:bg-white transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-red-500"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            ) : (
              <label className="cursor-pointer flex flex-col items-center group">
                <div className="p-4 bg-slate-100 rounded-full group-hover:bg-blue-100 transition-colors">
                  <ImageIcon className="w-8 h-8 text-slate-400 group-hover:text-blue-500" />
                </div>
                <span className="mt-3 text-slate-600 font-medium">
                  Klik untuk upload gambar
                </span>
                <input
                  type="file"
                  className="hidden"
                  onChange={handleImageUpload}
                  accept="image/*"
                />
              </label>
            )}
          </div>

          {/* Action Button */}
          <button
            onClick={describeImage}
            disabled={!image || loading}
            className={`w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all ${
              !image || loading
                ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700 shadow-md shadow-blue-100"
            }`}
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Menganalisis...
              </>
            ) : (
              <>
                <Camera className="w-5 h-5" />
                Dapatkan Deskripsi
              </>
            )}
          </button>

          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-100 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}

          {/* Output Area */}
          {description && (
            <div className="space-y-3 animate-in fade-in slide-in-from-bottom-2 duration-300">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">
                  Output (English)
                </h3>
                <button
                  onClick={copyToClipboard}
                  className="flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 bg-blue-50 px-3 py-1.5 rounded-full transition-colors"
                >
                  {copied ? (
                    <Check className="w-3.5 h-3.5" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                  {copied ? "Tersalin!" : "Salin Teks"}
                </button>
              </div>
              <div className="p-5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 leading-relaxed italic">
                {description}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Constraints Info */}
      <div className="max-w-2xl w-full mt-6 grid grid-cols-2 gap-4 text-xs text-slate-400 px-4">
        <div className="flex gap-2 italic">
          <span className="text-blue-400">•</span>
          Wajah, rambut, dan etnis akan diabaikan secara otomatis.
        </div>
        <div className="flex gap-2 italic text-right justify-end">
          Format: Subjek (image reference) + Deskripsi.
          <span className="text-blue-400">•</span>
        </div>
      </div>
    </div>
  );
};

export default App;
